import { Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { MatDividerModule} from '@angular/material/divider';
import { MatButtonModule} from '@angular/material/button';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormsModule } from '@angular/forms';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';

import { TopSubNav } from './shared/ui/layout/top-sub-nav/top-sub-nav';
import { RouterOutlet, RouterModule, Router, NavigationEnd } from '@angular/router';
import { Project } from './core/models/project-model';
import { toSignal } from '@angular/core/rxjs-interop';
import { ProjectService } from './core/projects/project';
import { CurrentUserService } from './services/current-user';

@Component({
  selector: 'app-root',
  imports: [
    MatDividerModule,
    MatButtonModule,
    TopSubNav,
    RouterOutlet,
    RouterModule,
    MatSnackBarModule,
    MatSelectModule,
    MatFormFieldModule,
    FormsModule,
  ],
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App implements OnInit, OnDestroy {
  readonly router = inject(Router);
  private readonly msalService = inject(MsalService);
  private readonly msalBroadcastService = inject(MsalBroadcastService);
  private readonly destroying$ = new Subject<void>();

  protected readonly title = signal('qs_app');
  readonly projectService = inject(ProjectService);
  readonly currentUserService = inject(CurrentUserService);

  projects: Project[] = [];

  // Signals für Projektdaten
  readonly projectsById = computed(() =>
    new Map(this.projectService.projects().map(p => [p.contractNr, this.projectService.mapProject(p)]))
  );

  /** Aktuell eingeloggter User – reaktiv via AvatarService */
  readonly currentUser = this.currentUserService.currentUser;

  currentUrl = toSignal(
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      map(() => this.router.url)
    ),
    { initialValue: this.router.url }
  );

  isProjectOverview = computed(() =>
    this.currentUrl() === '/qs/projects'
  );

  readonly selectedProjectCode = computed(() => {
    const url = this.currentUrl();
    const match = url.match(/\/qs\/project\/([^/]+)/);
    return match ? match[1] : null;
  });

  readonly selectedProject = computed(() => {
    const code = this.selectedProjectCode();
    if (code === null) return null;
    return this.projectsById().get(code) ?? null;
  });

  ngOnInit(): void {
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this.destroying$),
      )
      .subscribe(() => {
        this.setActiveAccount();
      });
  }

  ngOnDestroy(): void {
    this.destroying$.next();
    this.destroying$.complete();
  }

  private setActiveAccount(): void {
    const activeAccount = this.msalService.instance.getActiveAccount();
    if (!activeAccount) {
      const accounts = this.msalService.instance.getAllAccounts();
      if (accounts.length > 0) {
        this.msalService.instance.setActiveAccount(accounts[0]);
      }
    }

    // OID aus dem aktiven MSAL-Account setzen
    const account = this.msalService.instance.getActiveAccount();
    const oid = account?.idTokenClaims?.['oid'] as string | undefined;
    if (oid) {
      this.currentUserService.setCurrentUser(oid);
    }
  }
}
